import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CpExamination } from '../../models/custom-page';
import { Guid } from '@se/se.platform.base';
import { OpenReportModeType } from 'estensa-module';
import { first, flatten, groupBy, uniq, uniqBy } from 'lodash-es';
import { firstValueFrom, map, switchMap, tap } from 'rxjs';
import { CancellationError } from '../../confirm/confirm-dialog.service';
import type {
    ExaminationByOpenReportMode,
    IReportWorkflowExamService,
} from '../../services/report-workflow-exam.service';
import { RadioSettingsService } from '../../services/settings/radio-settings.service';
import { CpHttpService } from '../services/cp-http.service';
import { ModalDialogNotificationService, NotificationType } from '../../services/notification.service';
import { ButtonClick } from '../../dialogs/se-modalwindow/se-modalwindow.component';

export interface CpOpenableExamination {
    id: string;
    idCustomPage: string | null;
    idCustomPageRoot: string;
    idModelTypeRoots: string[];
    idReport: string;
    isApproved: boolean;
}

export interface ResultExamination {
    id: string;
    idReport: string;
    idCustomPage: string;
    idCustomPageRoots: string[];
    idModelTypeRoots: string[];
}

function openableExaminationFromCp(
    ex: CpExamination | CpOpenableExamination | ExaminationByOpenReportMode,
): ResultExamination {
    const idCustomPage = ex.idCustomPage === Guid.EMPTY ? null : ex.idCustomPage;

    return {
        id: ex.id,
        idCustomPage,
        idCustomPageRoots: [ex.idCustomPageRoot],
        idModelTypeRoots: ex.idModelTypeRoots,
        idReport: ex.idReport,
    };
}

export interface CpArgumentsResult {
    examinations: ResultExamination[];
    idCustomPageRoot: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class CpRouterDatabuilderService {
    constructor(
        private cpHttpService: CpHttpService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private settingsService: RadioSettingsService,
        private translate: TranslateService,
    ) {}

    async buildCpArguments(
        selectedItems: CpOpenableExamination[],
        examService: IReportWorkflowExamService,
        addExaminationsToReport: boolean,
    ) {
        const settings = await firstValueFrom(this.settingsService.radioSettingsAsync);
        const openReportMode = settings.worklistSettings.openReportMode;
        const examinations = await this.getExaminationsFromReport(selectedItems, examService);
        const multipleAccessionNumbers = settings.multipleAccessionNumbers;
        let result: CpArgumentsResult | null = null;
        try {
            if (
                // Se sono definitive o se non bisogna cercare le prestazioni associate, non fare altro.
                selectedItems.some((ex) => ex.isApproved) ||
                !this.shouldRetrieveAssociatedExaminations(
                    examinations,
                    examService,
                    openReportMode,
                    multipleAccessionNumbers,
                )
            ) {
                result = await this.resolveIdCustomPageRoot(examinations);
            } else {
                result = await this.getLinkedExaminations(
                    examinations,
                    examService,
                    openReportMode,
                    addExaminationsToReport,
                    multipleAccessionNumbers,
                );
            }
        } catch (error: unknown) {
            if (error instanceof CancellationError) {
                return null;
            }
            throw error;
        }

        if (result && result.idCustomPageRoot) {
            return result;
        }

        // Non esiste una pagina per refertare tutte le prestazioni selezionate.
        await firstValueFrom(
            this.modalDialogNotificationService.notify({
                buttonYes: false,
                buttonNo: false,
                buttonCancel: false,
                buttonOk: true,
                title: this.translate.instant('Warning'),
                message: this.translate.instant('WarningOpenExamsTypes'),
                type: NotificationType.warning,
            }),
        );
        return null;
    }

    private async getLinkedExaminations(
        examinations: ResultExamination[],
        examService: IReportWorkflowExamService,
        openReportMode: OpenReportModeType,
        addExaminationsToReport: boolean,
        multipleAccessionNumbers: boolean,
    ): Promise<CpArgumentsResult | null> {
        if (openReportMode === OpenReportModeType.SameIdEventOnlyNewReport) {
            return this.getExaminationsFromIdEventOnlyNewReport(examinations, examService);
        } else if (openReportMode === OpenReportModeType.Standard && multipleAccessionNumbers) {
            return this.getExaminationFromAccessionNumber(examinations, examService);
        } else {
            return this.getExaminationsFromSameIdEvent(examinations, examService, addExaminationsToReport);
        }
    }

    private async getExaminationsFromSameIdEvent(
        initialExaminations: ResultExamination[],
        examService: IReportWorkflowExamService,
        addExaminationsToReport: boolean,
    ): Promise<CpArgumentsResult | null> {
        const associatedExaminations = examService.retrieveAssociatedExaminations
            ? await firstValueFrom(
                  examService
                      .retrieveAssociatedExaminations(initialExaminations.map((s) => s.id))
                      .pipe(map((ex) => ex.map(openableExaminationFromCp))),
              )
            : [];

        const allExaminations = uniqBy(associatedExaminations.concat(initialExaminations), 'id');
        const result = await this.resolveIdCustomPageRoot(allExaminations);
        if (result.idCustomPageRoot) {
            if (addExaminationsToReport) {
                await this.addExaminationsToReport(allExaminations, examService);
            }
            return result;
        }

        return this.onSameIdEventNoCustomPageFound(initialExaminations);
    }

    private async getExaminationsFromIdEventOnlyNewReport(
        initialExaminations: ResultExamination[],
        examService: IReportWorkflowExamService,
    ): Promise<CpArgumentsResult> {
        const associatedExaminations = examService.retrieveAssociatedExaminations
            ? await firstValueFrom(
                  examService
                      .retrieveAssociatedExaminations(initialExaminations.map((s) => s.id))
                      .pipe(map((ex) => ex.map(openableExaminationFromCp))),
              )
            : [];

        const allExaminations = uniqBy(associatedExaminations.concat(initialExaminations), 'id');
        return this.resolveIdCustomPageRoot(allExaminations);
    }

    private async getExaminationFromAccessionNumber(
        initialExaminations: ResultExamination[],
        examService: IReportWorkflowExamService,
    ): Promise<CpArgumentsResult> {
        return this.getExaminationsFromIdEventOnlyNewReport(initialExaminations, examService);
    }

    private shouldRetrieveAssociatedExaminations(
        examinations: ResultExamination[],
        examService: IReportWorkflowExamService,
        openReportMode: OpenReportModeType,
        multipleAccessionNumbers: boolean,
    ) {
        return (
            // Il servizio non espone il metodo per recuperare le prestazioni associate (Visite, US, ...).
            examService.retrieveAssociatedExaminations &&
            // Modalità di apertura SameIdEvent.
            (openReportMode === OpenReportModeType.SameIdEvent ||
                // Modalità di apertura SameIdEventOnlyNewReport, ma non esiste ancora il referto.
                (openReportMode === OpenReportModeType.SameIdEventOnlyNewReport &&
                    !examinations.some((ex) => ex.idReport)) ||
                (openReportMode === OpenReportModeType.Standard &&
                    multipleAccessionNumbers &&
                    !examinations.some((ex) => ex.idReport)))
        );
    }

    private async addExaminationsToReport(examinations: ResultExamination[], examService: IReportWorkflowExamService) {
        const idExaminationsToAdd = examinations.filter((ex) => !ex.idReport).map((ex) => ex.id);
        const idReport = first(examinations.filter((ex) => ex.idReport))?.idReport;

        if (idExaminationsToAdd.length == 0 || !idReport || !examService.addExaminationsToReport) {
            return;
        }

        await firstValueFrom(
            examService
                .addExaminationsToReport({
                    idReport,
                    idExaminations: idExaminationsToAdd,
                })
                .pipe(
                    tap(() => {
                        examinations.forEach((ex) => {
                            ex.idReport = idReport;
                        });
                    }),
                    switchMap(() =>
                        this.modalDialogNotificationService.notify({
                            buttonYes: false,
                            buttonNo: false,
                            buttonCancel: false,
                            buttonOk: true,
                            title: this.translate.instant('Information'),
                            message: this.translate.instant('AssociatedExaminationsAddedToReport'),
                            type: NotificationType.information,
                        }),
                    ),
                ),
        );
    }

    private async getExaminationsFromReport(
        buildableExaminations: CpOpenableExamination[],
        examService: IReportWorkflowExamService,
    ): Promise<ResultExamination[]> {
        const examinationsByIdReport = groupBy(buildableExaminations, (ex) => ex.idReport || Guid.EMPTY);

        return Promise.all(
            Object.entries(examinationsByIdReport).map(async ([idReport, examinations]) => {
                const openableExaminations = examinations.map(openableExaminationFromCp);

                if (idReport === Guid.EMPTY || openableExaminations.every((ex) => ex.idCustomPage)) {
                    return Promise.resolve(openableExaminations);
                }

                const reportExaminations = await firstValueFrom(examService.getReportExaminations(idReport));
                for (const ex of openableExaminations) {
                    ex.idCustomPageRoots = uniq(
                        ex.idCustomPageRoots.concat(reportExaminations.map((ex) => ex.idCustomPageRoot)),
                    );
                }

                return openableExaminations;
            }),
        ).then((examinations) => flatten(examinations));
    }

    /**
     * Caso in cui il setting OpenReportMode è impostato a SameIdEvent e non esiste una custom page in grado di aprire
     *   tutte le prestazioni selezionate.
     */
    private async onSameIdEventNoCustomPageFound(examinations: ResultExamination[]): Promise<CpArgumentsResult | null> {
        // Se era stata selezionata una delle prestazioni del referto viene aperto il referto senza l'aggiunta della
        //   nuova prestazione.
        // Se era stata selezionata una singola prestazione che non fa parte del referto, questa viene aperta
        //   singolarmente.
        if (examinations.every((ex) => ex.idReport) || examinations.length === 1) {
            const confirmation = await firstValueFrom(
                this.modalDialogNotificationService.notify({
                    buttonCancel: false,
                    buttonNo: true,
                    buttonOk: false,
                    buttonYes: true,
                    message: this.translate.instant('CpSameIdEventNoCustomPageFound'),
                    title: this.translate.instant('Warning'),
                    type: NotificationType.warning,
                }),
            );
            if (confirmation === ButtonClick.yes) {
                return this.resolveIdCustomPageRoot(examinations);
            }
            throw new CancellationError();
        }

        // Se erano state selezionate più prestazioni non refertate, non è possibile aprire il referto.
        return null;
    }

    private async resolveIdCustomPageRoot(examinations: ResultExamination[]): Promise<CpArgumentsResult> {
        const idExaminations = examinations.map((ex) => ex.id);
        const uniqueIdCustomPageRoots = uniq(flatten(examinations.map((ex) => ex.idCustomPageRoots)));

        if (uniqueIdCustomPageRoots.length === 1) {
            return { examinations, idCustomPageRoot: uniqueIdCustomPageRoots[0] };
        }

        const idCustomPageRoot = await firstValueFrom(
            this.cpHttpService.resolveIdCustomPageRoot(uniqueIdCustomPageRoots, idExaminations),
        );

        return {
            examinations,
            idCustomPageRoot: idCustomPageRoot || null,
        };
    }
}
